/**
 * Home page illustrations (T853), ported from docs/design/html/lib/illustrations.mjs.
 * Inline SVG only, colours from design tokens (Tailwind token classes) so light and dark both work.
 * Every scene is decorative (the text next to it says the same) unless it carries its own label.
 * No animation: a static frame reads the same with prefers-reduced-motion and cannot be caught mid-fade.
 */
import { formatMoney } from "@stb/core/money"
import { CENTS_PER_KM_CAP_KM, calculateCentsPerKm } from "@stb/jurisdiction-au"
import { MapPin } from "lucide-react"
import { useId, useState } from "react"

import { useLocale } from "@/core/i18n/locale-context"

import { EXAMPLE_TRIP, HOME_FINANCIAL_YEAR, rate } from "./home-content"

const aud = (value: Parameters<typeof formatMoney>[0]) =>
  formatMoney(value, "en-AU").replace("$", "A$")

const hidden = { "aria-hidden": true, focusable: false } as const
export const card = "fill-card stroke-border"
export const t = "fill-foreground text-[14px] font-semibold"
export const s = "fill-muted-foreground text-[14px]"

/** The road drawn behind the hero (desktop only). Decorative. */
export const RouteBackdrop = () => (
  <svg
    {...hidden}
    viewBox="0 -40 640 400"
    preserveAspectRatio="xMidYMid slice"
    className="pointer-events-none absolute inset-0 -z-10 hidden size-full opacity-60 md:block"
  >
    <path
      d="M40 -10C60 80 110 150 230 200M330 360C330 280 350 200 396 150M-10 120H640M500 -10V360"
      className="stroke-border fill-none"
      strokeWidth="2"
    />
    <path
      d="M-20 300C90 300 120 196 230 200S396 120 452 80S560 40 640 34"
      className="stroke-muted fill-none"
      strokeWidth="34"
      strokeLinecap="round"
    />
    <path
      d="M-20 300C90 300 120 196 230 200S396 120 452 80S560 40 640 34"
      className="stroke-warning fill-none"
      strokeWidth="3"
      strokeDasharray="14 12"
    />
  </svg>
)

/** Phone showing the trip screen. One labelled image; the inner mock is hidden from assistive tech. */
export const PhoneTripMock = () => {
  const zh = useLocale() === "zh"
  return (
    <div
      role="img"
      aria-label={
        zh
          ? `行程卡片示例：${EXAMPLE_TRIP.route}，${EXAMPLE_TRIP.km} km，每公里 ${rate.cents} c，${EXAMPLE_TRIP.amount}，已标为业务行程。`
          : `Example trip card: ${EXAMPLE_TRIP.route}, ${EXAMPLE_TRIP.km} km at ${rate.cents} c/km, ${EXAMPLE_TRIP.amount}, marked Business.`
      }
      className="border-border bg-card text-foreground mx-auto w-full max-w-[300px] rounded-[2rem] border-4 p-2 shadow-lg md:justify-self-end"
    >
      <div aria-hidden="true" className="bg-background grid gap-3 rounded-3xl p-4">
        <div className="text-muted-foreground flex items-center justify-between text-sm">
          <b className="text-foreground">Trips</b>
          <span>Thu 8 Oct</span>
        </div>
        <div className="bg-primary/10 text-primary flex items-center gap-2 rounded-lg px-3 py-2 text-sm">
          <MapPin className="size-4" /> Trip saved. Review and classify it.
        </div>
        <svg viewBox="0 0 260 170" className="border-border bg-card w-full rounded-lg border">
          <path
            d="M0 40H260M0 110H260M70 0V170M190 0V170"
            className="stroke-border fill-none"
            strokeWidth="1"
          />
          <path
            d="M16 150C70 146 86 98 130 94S206 72 232 46"
            className="stroke-muted fill-none"
            strokeWidth="12"
            strokeLinecap="round"
          />
          <path
            d="M16 150C70 146 86 98 130 94S206 72 232 46"
            className="stroke-primary fill-none"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx="16" cy="150" r="7" className="fill-success" />
          <path
            d="M232 46c-8-9-13-15-13-23a13 13 0 0 1 26 0c0 8-5 14-13 23z"
            className="fill-primary"
          />
          <circle cx="232" cy="23" r="5" className="fill-warning" />
        </svg>
        <div>
          <span className="text-3xl font-extrabold tabular-nums">{EXAMPLE_TRIP.km}</span>{" "}
          <span className="text-muted-foreground">km</span>
        </div>
        <div className="flex items-end justify-between gap-2 text-sm">
          <span>
            <b className="block">{EXAMPLE_TRIP.route}</b>
            <span className="text-muted-foreground">
              {EXAMPLE_TRIP.km} km × {rate.cents} c/km
            </span>
          </span>
          <span className="grid justify-items-end gap-1">
            <span className="bg-success/15 text-success rounded-full px-2 py-0.5 text-xs font-medium">
              You: Business
            </span>
            <b className="text-lg">{EXAMPLE_TRIP.amount}</b>
          </span>
        </div>
      </div>
    </div>
  )
}

export const Scene = ({ viewBox, children }: { viewBox: string; children: React.ReactNode }) => (
  <svg {...hidden} viewBox={viewBox} className="h-auto w-full max-w-[420px]" role="presentation">
    {children}
  </svg>
)

/** A GPS trip on a map grid with distance chip and place chip. */
export const TripIllustration = () => (
  <Scene viewBox="0 0 320 200">
    <rect x="1" y="1" width="318" height="198" rx="12" className={card} strokeWidth="1.5" />
    <path
      d="M1 50H319M1 100H319M1 150H319M60 1V199M130 1V199M200 1V199M270 1V199"
      className="stroke-border fill-none"
    />
    <path
      d="M1 156C80 146 120 80 200 84S290 62 319 44M112 199C122 152 146 112 200 84"
      className="stroke-muted fill-none"
      strokeWidth="14"
      strokeLinecap="round"
    />
    <path
      d="M48 152C92 146 122 98 168 96S236 86 262 66"
      className="stroke-primary fill-none"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <circle cx="48" cy="152" r="7" className="fill-success" />
    <path d="M262 66c-9-10-15-17-15-26a15 15 0 0 1 30 0c0 9-6 16-15 26z" className="fill-primary" />
    <circle cx="262" cy="40" r="5.5" className="fill-warning" />
    <rect x="16" y="16" width="140" height="54" rx="8" className={card} strokeWidth="1.5" />
    <text x="28" y="41" className={`${t} text-[18px]`}>
      {EXAMPLE_TRIP.km} km
    </text>
    <text x="28" y="60" className={s}>
      Business · {EXAMPLE_TRIP.amount}
    </text>
    <rect x="16" y="164" width="196" height="26" rx="8" className={card} strokeWidth="1.5" />
    <text x="28" y="182" className={s}>
      Merrylands to Parramatta
    </text>
  </Scene>
)

/** A receipt with the four fields read from it; the GST row is highlighted. */
export const ReceiptIllustration = () => {
  const fields = [
    ["Merchant", "Bunnings Wetherill Park"],
    ["Date", "Wed 7 Oct 2026"],
    ["Total", "A$92.95"],
    ["GST", "A$8.45"],
  ]
  return (
    <Scene viewBox="0 0 340 210">
      <path
        d="M12 20a8 8 0 0 1 8-8h80a8 8 0 0 1 8 8v172l-8 6-8-6-8 6-8-6-8 6-8-6-8 6-8-6-8 6-8-6-8 6-8-6-8 6V20z"
        className={card}
        strokeWidth="1.5"
      />
      <rect x="24" y="28" width="60" height="8" rx="4" className="fill-foreground/70" />
      <rect x="24" y="44" width="40" height="6" rx="3" className="fill-muted" />
      <path d="M24 72H96M24 86H88M24 100H92" className="stroke-border fill-none" strokeWidth="2" />
      <rect x="8" y="124" width="104" height="3" rx="1.5" className="fill-primary" />
      <rect x="24" y="140" width="72" height="7" rx="3.5" className="fill-muted" />
      <path d="M108 32H122M108 158H122" className="stroke-border fill-none" />
      {fields.map(([k, v], n) => {
        const y = 12 + n * 48
        const on = n === 3
        return (
          <g key={k}>
            <rect
              x="122"
              y={y}
              width="206"
              height="40"
              rx="8"
              className={on ? "fill-primary/15 stroke-primary" : card}
              strokeWidth="1.5"
            />
            <text x="134" y={y + 16} className={s}>
              {k}
            </text>
            <text x="134" y={y + 33} className={t}>
              {v}
            </text>
            {on && (
              <>
                <circle cx="310" cy={y + 20} r="9" className="fill-success" />
                <path
                  d={`M306 ${y + 20}l3 3 5-6`}
                  className="stroke-background fill-none"
                  strokeWidth="2"
                />
              </>
            )}
          </g>
        )
      })}
    </Scene>
  )
}

export interface InvoiceScene {
  no: string
  to: string
  lines: [string, string][]
  subtotal: string
  gst: string
  total: string
}

const HOME_INVOICE: InvoiceScene = {
  no: "INV-0049",
  to: "Harbour Strata",
  lines: [
    ["Labour, fleet service", "A$570.00"],
    ["Brake pads", "A$145.65"],
    ["Workshop sundries", "A$12.35"],
    ["Inspection fee (GST-free)", "A$120.00"],
  ],
  subtotal: "A$848.00",
  gst: "A$72.80",
  total: "A$920.80",
}

/** A tax invoice card: header, line items, subtotal, GST band, total (default: home demo INV-0049, A$920.80). */
export const InvoiceIllustration = ({ invoice = HOME_INVOICE }: { invoice?: InvoiceScene }) => {
  const { lines } = invoice
  const y0 = 92
  const yS = y0 + lines.length * 22 + 6
  return (
    <Scene viewBox={`0 0 320 ${yS + 82}`}>
      <rect x="1" y="1" width="318" height={yS + 80} rx="12" className={card} strokeWidth="1.5" />
      <text x="20" y="34" className={`${t} text-[18px]`}>
        Tax invoice
      </text>
      <text x="300" y="34" textAnchor="end" className={s}>
        {invoice.no}
      </text>
      <text x="20" y="54" className={s}>
        To {invoice.to}
      </text>
      <path d={`M20 68H300M20 ${yS - 8}H300`} className="stroke-border" />
      {lines.map(([d, a], n) => (
        <g key={d}>
          <text x="20" y={y0 + n * 22} className={s}>
            {d}
          </text>
          <text x="300" y={y0 + n * 22} textAnchor="end" className={t}>
            {a}
          </text>
        </g>
      ))}
      <text x="20" y={yS + 12} className={s}>
        Subtotal
      </text>
      <text x="300" y={yS + 12} textAnchor="end" className={t}>
        {invoice.subtotal}
      </text>
      <rect x="12" y={yS + 22} width="296" height="28" rx="6" className="fill-primary/15" />
      <text x="20" y={yS + 41} className={t}>
        GST
      </text>
      <text x="300" y={yS + 41} textAnchor="end" className={t}>
        {invoice.gst}
      </text>
      <text x="20" y={yS + 70} className={`${t} text-[18px]`}>
        Total
      </text>
      <text x="300" y={yS + 70} textAnchor="end" className={`${t} text-[18px]`}>
        {invoice.total}
      </text>
    </Scene>
  )
}

/** Shield and lock in front of kept records. Decorative: the list next to it says the same. */
export const ShieldIllustration = () => (
  <Scene viewBox="0 0 160 190">
    <rect
      x="14"
      y="36"
      width="72"
      height="96"
      rx="6"
      className={card}
      strokeWidth="1.5"
      transform="rotate(-8 50 84)"
    />
    <rect
      x="58"
      y="30"
      width="72"
      height="96"
      rx="6"
      className={card}
      strokeWidth="1.5"
      transform="rotate(7 94 78)"
    />
    <path
      d="M72 22l46 17v44c0 34-20 57-46 70-26-13-46-36-46-70V39z"
      className="fill-primary/15 stroke-primary"
      strokeWidth="2"
    />
    <path d="M62 92v-9a10 10 0 0 1 20 0v9" className="stroke-primary fill-none" strokeWidth="4" />
    <rect x="56" y="90" width="32" height="26" rx="5" className="fill-primary" />
    <circle cx="72" cy="102" r="3.5" className="fill-background" />
  </Scene>
)

const STEP = 100

const SLIDER_TEXT = {
  en: {
    title: "Cents per km",
    label: "Business kilometres this income year:",
    cap: "km cap",
    note: "Estimate only. Not tax advice.",
  },
  zh: {
    title: "cents per km 法",
    label: "本收入年度的业务公里数：",
    cap: "km 上限",
    note: "仅为估算，不是税务建议。",
  },
} as const

/** Cents-per-km slider: km × the year's rate in whole cents (core/money), capped at the ATO limit. */
export const CentsSlider = () => {
  const id = useId()
  const t = SLIDER_TEXT[useLocale()]
  const [km, setKm] = useState(3200)
  const result = calculateCentsPerKm(HOME_FINANCIAL_YEAR, km)
  // The slider max is the cap, so the over-cap result cannot happen.
  if (!result.ok) return null
  const amount = aud(result.deduction)
  const kmLabel = km.toLocaleString("en-AU")
  return (
    <div className="border-border bg-card grid gap-4 rounded-xl border p-5">
      <div className="flex items-baseline justify-between gap-3">
        <b className="text-lg">{t.title}</b>
        <span className="text-muted-foreground text-sm">{rate.cents} c/km</span>
      </div>
      <div className="grid gap-2">
        <label htmlFor={id} className="text-sm font-medium">
          {t.label} <b className="tabular-nums">{kmLabel} km</b>
        </label>
        <input
          id={id}
          type="range"
          min={0}
          max={CENTS_PER_KM_CAP_KM}
          step={STEP}
          value={km}
          onChange={(e) => setKm(Number(e.target.value))}
          aria-valuetext={`${kmLabel} km`}
          className="accent-primary focus-visible:ring-ring h-11 w-full cursor-pointer rounded-sm outline-none focus-visible:ring-2"
        />
        <div className="text-muted-foreground flex justify-between text-sm">
          <span>0 km</span>
          <span>
            {CENTS_PER_KM_CAP_KM.toLocaleString("en-AU")} {t.cap}
          </span>
        </div>
      </div>
      <p
        role="status"
        className="bg-primary/10 rounded-lg px-4 py-3 text-center font-semibold tabular-nums"
      >
        {kmLabel} km × {rate.cents} c = {amount}
      </p>
      <p className="text-muted-foreground text-sm">{t.note}</p>
    </div>
  )
}
